import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LumivetaLogo from '../../components/LumivetaLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import api from '../../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters with a number');
      return;
    }

    setLoading(true);

    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={32} color="#EF4444" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
            Invalid Reset Link
          </h2>
          <p className="text-muted-foreground mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link to="/forgot-password">
            <Button variant="default">Request New Reset Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - Lumiveta</title>
        <meta name="description" content="Set a new password for your Lumiveta account" />
      </Helmet>

      <div className="min-h-dvh bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <LumivetaLogo size="large" />
            </Link>
          </div>

          <div className="glass rounded-2xl p-8 border border-primary/20">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} color="#10B981" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                  Password Reset!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your password has been reset successfully. Redirecting to login...
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon name="Lock" size={32} color="#FFD700" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    Set New Password
                  </h2>
                  <p className="text-muted-foreground">
                    Enter your new password below
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        className="bg-input border-border pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">At least 8 characters with a number</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="bg-input border-border pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    disabled={loading}
                    className="gold-glow"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
          </p>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
